/**
 * The leaderboard: players who chose to be public, on two boards — by level
 * (how well they play) and by points (how much) — and where you stand.
 * Everything on it comes from `/api/players/` — see `worker/players.js` for how
 * each is worked out and what keeps it honest, and `../level.js` for the level.
 */

import { mountNav } from '../nav.mjs';
import { accountUser, playerUrl } from '../account-link.js';
import { RUNG_LEVEL, formatLevel } from '../level.js';
import { LADDER } from '../play/opponent.js';

const $ = (id) => document.getElementById(id);
mountNav($('nav-slot'), { here: 'leaderboard', base: '../' });

const API = new URL('../api/players/', import.meta.url);
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const num = (n) => Number(n || 0).toLocaleString();

async function get(path) {
  const response = await fetch(new URL(path, API), { credentials: 'same-origin' });
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
}

/** The rungs on the scale, in the rules — out of the ladder, so they cannot drift from it. */
$('lb-rungs').textContent = `The engine's rungs sit on it: ${LADDER.filter((r) => RUNG_LEVEL[r.id] != null)
  .map((r) => `${r.id === 'full' ? 'the full engine' : r.name.split(' ').at(-1)} ${RUNG_LEVEL[r.id].toFixed(1)}`).join(', ')}.`;

const medal = (rank) => (rank <= 3 ? `<span class="lb-medal m${rank}">${rank}</span>` : rank);
const levelCell = (p) => (p.level != null ? formatLevel(p.level) : '<span class="lb-dim">—</span>');

/** Each board's columns: heading, class, and the cell. */
const BOARDS = {
  level: {
    say: (n, min) => (n
      ? `${num(n)} ${n === 1 ? 'player' : 'players'} with a public profile and ${min} rated games or more.`
      : `Nobody is on the level board yet: it takes ${min} rated games and a public profile.`),
    columns: [
      ['#', 'n', (p) => medal(p.rank)],
      ['Player', '', (p) => `<a href="${esc(playerUrl(p.username))}">${esc(p.username)}</a>`],
      ['Level', 'n lb-level', levelCell],
      ['Rated games', 'n lb-wide', (p) => num(p.levelGames)],
      ['Won · drawn · lost', 'n lb-wide', (p) => `${num(p.wins)} · ${num(p.draws)} · ${num(p.losses)}`],
    ],
  },
  points: {
    say: (n) => (n
      ? `${num(n)} ${n === 1 ? 'player' : 'players'} with a public profile.`
      : 'Nobody is on the board yet. Choose a username, make your profile public, and be the first.'),
    columns: [
      ['#', 'n', (p) => medal(p.rank)],
      ['Player', '', (p) => `<a href="${esc(playerUrl(p.username))}">${esc(p.username)}</a>`],
      ['Score', 'n lb-score', (p) => num(p.score)],
      ['Level', 'n lb-wide', levelCell],
      ['Won · drawn · lost', 'n lb-wide', (p) => `${num(p.wins)} · ${num(p.draws)} · ${num(p.losses)}`],
      ['Likes', 'n', (p) => num(p.liked)],
    ],
  },
};

const loaded = {};
let board = location.hash === '#points' ? 'points' : 'level';

function drawTop(which, data, me) {
  const spec = BOARDS[which];
  const { players } = data;
  $('lb-say').textContent = spec.say(players.length, data.min);
  $('lb-table').hidden = !players.length;
  $('lb-table').querySelector('thead tr').innerHTML = spec.columns
    .map(([label, cls]) => `<th class="${cls}" scope="col">${esc(label)}</th>`).join('');
  $('lb-table').querySelector('tbody').innerHTML = players.map((p) => `
    <tr${me && p.username === me ? ' class="lb-me"' : ''}>${spec.columns
    .map(([, cls, cell]) => `<td class="${cls}">${cell(p)}</td>`).join('')}</tr>`).join('');
}

async function show(which, { remember = true } = {}) {
  board = which;
  for (const tab of document.querySelectorAll('.lb-board')) {
    tab.setAttribute('aria-selected', String(tab.dataset.board === which));
  }
  if (remember) history.replaceState(null, '', which === 'points' ? '#points' : location.pathname + location.search);
  try {
    loaded[which] ??= get(which === 'level' ? 'top/level' : 'top');
    const data = await loaded[which];
    const user = await accountUser;
    if (board === which) drawTop(which, data, user?.username);
  } catch {
    delete loaded[which];
    $('lb-say').textContent = 'The leaderboard could not be loaded. Try again in a moment.';
  }
}

for (const tab of document.querySelectorAll('.lb-board')) {
  tab.addEventListener('click', () => show(tab.dataset.board));
}

/** Where you stand, or what it would take to be on here at all. */
async function drawYou(user) {
  const box = $('lb-you');
  box.hidden = false;
  if (!user) {
    box.innerHTML = '<h3>Your place</h3><p class="pf-say"><a href="../account/#signup">Create an account</a> or '
      + '<a href="../account/">sign in</a>, choose a username, and every game you play here counts.</p>';
    return;
  }
  if (!user.username) {
    box.innerHTML = '<h3>Your place</h3><p class="pf-say"><a href="../account/">Choose a username</a> to get a level and a score.</p>';
    return;
  }
  let me;
  try { me = (await get(`u/${encodeURIComponent(user.username)}`)).player; } catch { box.hidden = true; return; }
  const lv = me.level || { level: 0, games: 0 };
  box.innerHTML = `<h3>Your place</h3>
    <div class="lb-you-row">
      <div><span class="lb-big">${formatLevel(lv.level)}</span><span class="lb-label">${lv.rank ? `level · #${num(lv.rank)}` : 'level'}</span></div>
      <div><span class="lb-big">${num(me.total)}</span><span class="lb-label">score</span></div>
      <div><span class="lb-big">${me.rank ? `#${num(me.rank)}` : '—'}</span><span class="lb-label">${me.public ? 'by score' : 'private'}</span></div>
    </div>
    <p class="pf-say">${me.public
    ? `You are on the board as <a href="${esc(playerUrl(me.username))}">${esc(me.username)}</a>.`
      + (lv.games < lv.boardMin ? ` The level board needs ${num(lv.boardMin)} rated games; you have ${num(lv.games)}.` : '')
    : 'Your profile is private, so you are not on the board. <a href="../account/">Make it public</a> to join it.'}</p>`;
}

accountUser.then((user) => drawYou(user));

// The level board first — unless nobody is on it yet, when an empty table
// would be the first thing a visitor saw. Then the points board, which fills
// as soon as anybody plays.
if (board === 'level') {
  get('top/level').then((data) => {
    loaded.level = Promise.resolve(data);
    show(data.players.length ? 'level' : 'points', { remember: false });
  }).catch(() => show('points', { remember: false }));
} else {
  show('points', { remember: false });
}
